import { useQuery } from 'react-query';
import { useEffect } from 'react';
import Head from 'next/head';
import Icon from 'components/common/Icon';
import PostDetails from 'components/PostDetails';
import PostDetailsMobile from 'components/PostDetailsMobile';
import useStore from 'store/timeline';
import useGlobalStore from 'store/global';
import { SITE_BASE_URL } from 'config/env';
import { NextPageContext } from 'next';
import styles from './PostDetailsPage.module.scss';
import { getPostDetail } from 'service/post';
import { GetPostResult, Post } from 'common/interfaces/api';
import { useState } from 'react';
import { getTippedOfPost } from 'service/payment';
import {
  ogpDataOfPost,
  stringifyJSON,
  useMediaQuery,
  parseJSON,
} from 'common/utils';
import { generateCookieList } from 'common/utils/trackingTag';
import { parse } from 'cookie';

type PostDetailsPageProps = {
  username: string;
  postId: string;
  post: string | null;
};

const PostDetailsPage = (props: PostDetailsPageProps) => {
  const userName = props.username;
  const postId = props.postId;
  const [post, setPost] = useState(parseJSON(props.post) as Post);
  const shouldRefetchPost = useStore((state) => state.shouldRefetchPost);
  const shouldRefetchTippedListOfPost = useStore(
    (state) => state.shouldRefetchTippedListOfPost
  );
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const [loadedPostWithAuth, setLoadedPostWithAuth] = useState(false);
  const { refetch, isError, isLoading, isFetching } = useQuery(
    ['getPost', postId?.toString()],
    async () => {
      if (!postId) return Promise.reject();
      return getPostDetail(postId);
    },
    {
      enabled: isAuthInit && !!postId,
      retry: false,
      onSuccess: (data) => {
        const post = data?.data;
        setPost(post);
        setTimeout(() => {
          setLoadedPostWithAuth(true);
        }, 500);
      },
      onError: () => {
        setLoadedPostWithAuth(true);
      },
      refetchOnWindowFocus: false,
    }
  );

  const { refetch: refetchTippedList, data: tippedData } = useQuery(
    ['getTippedListOfPost', post?.id?.toString()],
    async () => {
      if (!post) return Promise.reject();
      return getTippedOfPost(post.id, 1);
    },
    {
      enabled: isAuthInit && !!post,
      refetchOnWindowFocus: false,
    }
  );

  const tippedListOfPost = tippedData?.data?.tipped_users || [];
  const tippedCount = tippedData?.data?.count || 0;
  useEffect(() => {
    if (shouldRefetchPost) {
      void refetch();
    }
  }, [shouldRefetchPost, refetch]);

  useEffect(() => {
    if (shouldRefetchTippedListOfPost) {
      void refetchTippedList();
    }
  }, [shouldRefetchTippedListOfPost, refetchTippedList]);

  // Validate the current URL is followed `/[owner]/posts/[postId]`
  const isValidUrl = post?.user_info?.username === userName;
  // Metadata
  const user = post?.user_info;
  const baseTitleText =
    (user?.name ? user.name : '') + (user ? ' | ' : '') + 'PostPrime';

  const ogpData = ogpDataOfPost(post);
  const titleText = ogpData.title
    ? `${ogpData.title} | ${baseTitleText}`
    : baseTitleText;
  return (
    <>
      <Head>
        {/* HTML Meta Tags */}
        <title>{titleText}</title>
        <meta
          name="description"
          content={
            ogpData.description
              ? `${ogpData.description} ...`
              : 'ログインして、SNSでお金を学ぼう!'
          }
        />
        {(ogpData.keywords || []).length > 0 && (
          <meta name="keywords" content={(ogpData.keywords || []).join(', ')} />
        )}
        {/* Facebook Meta Tags */}
        <meta
          property="og:url"
          content={`${SITE_BASE_URL}/${userName}/posts/${postId}`}
          key="ogurl"
        />
        <meta
          property="og:type"
          content={ogpData.videoInfo ? 'video.other' : 'article'}
          key="ogtype"
        />
        <meta property="og:title" content={titleText} />
        <meta
          property="og:description"
          content={
            ogpData.description
              ? `${ogpData.description} ...`
              : 'ログインして、SNSでお金を学ぼう!'
          }
        />
        {ogpData.imageUrl && (
          <meta
            property="og:image"
            content={ogpData.imageUrl || ''}
            key="ogimage"
          />
        )}
        {/* Video tag */}
        {ogpData.videoInfo && (
          <>
            <meta property="og:video:url" content={ogpData.videoInfo?.url} />
            <meta
              property="og:video:secure_url"
              content={ogpData.videoInfo?.url}
            />
            <meta property="og:video:type" content={ogpData.videoInfo?.type} />
            <meta
              property="og:video:width"
              content={ogpData.videoInfo?.width?.toString()}
            />
            <meta
              property="og:video:height"
              content={ogpData.videoInfo?.height?.toString()}
            />
            {(ogpData.keywords || []).map((keyword, index) => (
              <meta
                key={`${keyword}-${index}`}
                property="og:video:tag"
                content={keyword}
              />
            ))}
          </>
        )}
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content={`${SITE_BASE_URL}/${userName}/posts/${postId}`}
          key="twurl"
        />
        <meta name="twitter:title" content={titleText} />
        <meta
          name="twitter:description"
          content={
            ogpData.description
              ? `${ogpData.description} ...`
              : 'ログインして、SNSでお金を学ぼう!'
          }
        />
        {ogpData.imageUrl && (
          <meta
            name="twitter:image"
            content={ogpData.imageUrl || ''}
            key="twimage"
          />
        )}
        <link
          rel="canonical"
          href={`${SITE_BASE_URL}/${userName}/posts/${postId}`}
        />
      </Head>
      {(!isValidUrl || isError) && !isLoading && !isFetching ? (
        <div className={styles.emptyWrapper}>
          <Icon name="empty" width={96} height={96} />
          <div className={styles.emptyText}>この投稿は存在しません</div>
        </div>
      ) : (
        post &&
        (isMobile ? (
          <PostDetailsMobile
            post={post}
            tippedListOfPost={tippedListOfPost}
            tippedCount={tippedCount}
            loadedPostWithAuth={loadedPostWithAuth}
          />
        ) : (
          <PostDetails
            post={post}
            tippedListOfPost={tippedListOfPost}
            tippedCount={tippedCount}
            loadedPostWithAuth={loadedPostWithAuth}
          />
        ))
      )}
    </>
  );
};

export const getServerSideProps = async (context: NextPageContext) => {
  const username = context.query.username as string;
  const postId = context.query.postId as string;
  const cookiesList = generateCookieList(
    context.query,
    parse(context.req?.headers?.cookie || '')
  );
  if (cookiesList.length > 0) {
    context.res.setHeader('Set-Cookie', cookiesList);
  }
  let postData: GetPostResult;
  try {
    postData = await getPostDetail(postId, true);
    const isValidUrl = postData?.data?.user_info?.username === username;
    if (!isValidUrl) {
      return {
        props: {
          username: username,
          postId: postId,
          post: null,
        },
      };
    }
    return {
      props: {
        username: username,
        postId: postId,
        post: stringifyJSON(postData?.data) as string,
      },
    };
  } catch (error) {
    return {
      props: {
        username: username,
        postId: postId,
        post: null,
      },
    };
  }
};

export default PostDetailsPage;
